import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../../state/useAuth';
import { useData } from '../../../context/DataContext';
import { useNavigate } from 'react-router-dom';
import { Smartphone } from 'lucide-react';

export default function WorkerProfil() {
  const { model: user, logout } = useAuth();
  const { assignments } = useData();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ total: 0, done: 0, inProgress: 0 });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone);

  useEffect(() => {
    if (!user || !assignments) return;

    // Récupération intelligente (prend en charge les multi-techniciens et la vue globale)
    const filtered = assignments.filter(a => {
      if (user.can_view_all_planning) return true;
      const u = a.user;
      if (Array.isArray(u)) return u.some(id => (typeof id === 'string' ? id : id.id) === user.id);
      return (typeof u === 'string' ? u : u?.id) === user.id;
    });

    const doneCount = filtered.filter(a => a.expand?.chantier?.status === 'completed').length;
    const inProgCount = filtered.filter(a => a.expand?.chantier?.status === 'in_progress').length;

    setStats({ total: filtered.length, done: doneCount, inProgress: inProgCount });
  }, [user, assignments]); 
    
  useEffect(() => {
    // PWA Logic
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, []);

  const handleInstallClick = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    } else {
      alert("Sur iPhone/iPad : Appuyez sur le bouton central 'Partager' en bas de l'écran, puis glissez vers le bas et choisissez 'Sur l'écran d'accueil'.");
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const initials = (user?.name || user?.username || 'A').slice(0, 2).toUpperCase();
  const rate = stats.total > 0 ? Math.round((stats.done / stats.total) * 100) : 0;

  const getBadge = () => {
    if (rate >= 90) return { label: '⭐ Expert Terrain', color: '#f59e0b', bg: '#fffbeb' };
    if (rate >= 70) return { label: '🔧 Technicien Pro', color: 'var(--w-accent-dark)', bg: 'var(--w-accent-light)' };
    if (rate >= 40) return { label: '🛠️ Technicien', color: '#3b82f6', bg: '#eff6ff' };
    return { label: '🚀 En formation', color: '#6366f1', bg: '#eef2ff' };
  };
  const badge = getBadge();

  return (
    <div>
      {/* Header Card — Light */}
      <div style={{
        background: '#fff',
        borderBottom: '1px solid var(--w-border)',
        padding: '32px 20px 24px', textAlign: 'center',
      }}>
        <div style={{
          width: 80, height: 80, borderRadius: 28,
          background: 'var(--w-accent-light)',
          border: '2px solid var(--w-accent)',
          display: 'grid', placeItems: 'center',
          fontSize: 30, fontWeight: 900, color: 'var(--w-accent)',
          margin: '0 auto 14px',
          boxShadow: '0 4px 16px rgba(34,197,94,0.15)'
        }}>
          {initials}
        </div>
        <h2 style={{ margin: '0 0 4px', color: 'var(--w-text)', fontSize: 20, fontWeight: 800 }}>
          {user?.name || user?.username}
        </h2>
        <p style={{ margin: '0 0 14px', color: 'var(--w-muted)', fontSize: 13 }}>
          {user?.email}
        </p>
        <span style={{
          display: 'inline-block', padding: '5px 14px', borderRadius: 99,
          background: badge.bg, color: badge.color, fontWeight: 800, fontSize: 12,
          border: `1px solid ${badge.color}22`
        }}>
          {badge.label}
        </span>
      </div>

      <div style={{ padding: '16px' }}>

        {/* PWA Install App Button */}
        {!isInstalled && (
          <button 
            onClick={handleInstallClick}
            className="w-card"
            style={{ 
              width: '100%', padding: '16px', marginBottom: 20, 
              background: '#0f172a', border: '1px solid #1e293b', color: '#fff', 
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              cursor: 'pointer', textAlign: 'left',
              boxShadow: '0 8px 20px -8px rgba(0,0,0,0.5)'
            }}
          >
             <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                <div style={{ width: 40, height: 40, background: 'linear-gradient(135deg, #38bdf8 0%, #0284c7 100%)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(56, 189, 248, 0.3)' }}>
                  <Smartphone size={20} color="#fff" />
                </div>
                <div>
                   <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', marginBottom: 2, letterSpacing: '-0.3px' }}>Installer l'Application</div>
                   <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600 }}>Un raccourci sur votre téléphone</div>
                </div>
             </div>
             <div style={{ background: '#38bdf8', color: '#fff', padding: '6px 14px', borderRadius: 20, fontSize: 11, fontWeight: 900, textTransform: 'uppercase' }}>
                Installer
             </div>
          </button>
        )}

        {/* Stats */}
        <p className="w-section-title" style={{ marginBottom: 12 }}>Statistiques du mois</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20 }}>
          {[
            { label: 'Missions', value: stats.total, color: '#3b82f6', bg: '#eff6ff' },
            { label: 'Terminées', value: stats.done, color: 'var(--w-accent-dark)', bg: 'var(--w-accent-light)' },
            { label: 'En cours', value: stats.inProgress, color: '#f59e0b', bg: '#fffbeb' },
          ].map(s => (
            <div key={s.label} className="w-card" style={{ padding: '16px 10px', textAlign: 'center' }}>
              <div style={{ fontSize: 28, fontWeight: 900, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--w-muted)', marginTop: 4 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Completion bar */}
        {stats.total > 0 && (
          <div className="w-card" style={{ padding: '16px', marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--w-text)' }}>Taux de complétion</span>
              <span style={{ fontSize: 13, fontWeight: 800, color: 'var(--w-accent)' }}>{rate}%</span>
            </div>
            <div style={{ height: 8, background: '#f1f5f9', borderRadius: 99, overflow: 'hidden' }}>
              <div style={{
                height: '100%', width: `${rate}%`, borderRadius: 99,
                background: 'linear-gradient(90deg, var(--w-accent), #16a34a)',
                transition: 'width 1s ease'
              }} />
            </div>
          </div>
        )}

        {/* Info */}
        <div className="w-card" style={{ marginBottom: 20, overflow: 'hidden' }}>
          {[
            { label: 'Nom', value: user?.name || '—' },
            { label: 'Identifiant', value: `@${user?.username}` },
            { label: 'Email', value: user?.email || '—' },
            { label: 'Rôle', value: 'Salarié / Chantier' },
          ].map((row, i, arr) => (
            <div key={row.label} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '13px 16px',
              borderBottom: i < arr.length - 1 ? '1px solid var(--w-border)' : 'none'
            }}>
              <span style={{ fontSize: 13, color: 'var(--w-muted)' }}>{row.label}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--w-text)' }}>{row.value}</span>
            </div>
          ))}
        </div>

        {/* Logout */}
        <button
          onClick={handleLogout}
          className="w-btn-secondary"
          style={{ color: '#ef4444', borderColor: '#fecaca', background: '#fff5f5' }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
          Se déconnecter
        </button>
      </div>
    </div>
  );
}
